import React from 'react';
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

const buttonVariants = {
    variants: {
        variant: {
            default: "bg-primary text-primary-foreground hover:bg-primary/90",
            destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
            outline: "border border-input hover:bg-accent hover:text-accent-foreground",
            secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
            ghost: "hover:bg-accent hover:text-accent-foreground",
            link: "text-primary underline-offset-4 hover:underline",
            success: "bg-success text-success-foreground hover:bg-success/90",
            warning: "bg-warning text-warning-foreground hover:bg-warning/90",
            danger: "bg-error text-error-foreground hover:bg-error/90",
        },
        size: {
            default: "h-10 px-4 py-2",
            sm: "h-9 px-3",
            lg: "h-11 px-8",
            icon: "h-10 w-10",
            xs: "h-8 px-2 text-xs",
            xl: "h-12 px-10 text-base",
        },
    },
    defaultVariants: {
        variant: "default",
        size: "default",
    },
};

const getVariantClasses = (variant = 'default', size = 'default') => {
    const baseClasses = "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
    const variantClass = buttonVariants?.variants?.variant?.[variant] || buttonVariants?.variants?.variant?.default;
    const sizeClass = buttonVariants?.variants?.size?.[size] || buttonVariants?.variants?.size?.default;
    
    return `${baseClasses} ${variantClass} ${sizeClass}`;
};

const Button = React.forwardRef(({
    className = "",
    variant = "default",
    size = "default",
    children,
    loading = false,
    fullWidth = false,
    disabled = false,
    onClick,
    asChild = false,
    iconName,
    iconPosition,
    ...props
}, ref) => {
    // Loading spinner
    const LoadingSpinner = () => (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    const handleClick = (e) => {
        if (!disabled && !loading && onClick) {
            onClick(e);
        }
    };

    const buttonClasses = cn(
        getVariantClasses(variant, size),
        fullWidth && "w-full",
        className
    );

    // If asChild is true, render children directly with the button classes
    if (asChild) {
        return React.cloneElement(children, {
            className: cn(buttonClasses, children.props.className),
            disabled: disabled || loading,
            onClick: handleClick,
            ...props
        });
    }

    // Render icon if provided
    const renderIcon = () => {
        if (!iconName) return null;
        
        const iconElement = <Icon name={iconName} size={16} />;
        
        if (iconPosition === 'left') {
            return <span className="mr-2">{iconElement}</span>;
        } else if (iconPosition === 'right') {
            return <span className="ml-2">{iconElement}</span>;
        }
        
        return iconElement;
    };

    return (
        <button
            className={buttonClasses}
            ref={ref}
            disabled={disabled || loading}
            onClick={handleClick}
            {...props}
        >
            {loading && <LoadingSpinner />}
            {iconPosition === 'left' && renderIcon()}
            {children}
            {iconPosition === 'right' && renderIcon()}
        </button>
    );
});

Button.displayName = "Button";
export default Button;